
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { IStore, IViewPort } from '../../interfaces';
import { IMatchScarvesGroup, EnumSection } from '../../interfaces/clientInterfaces';
import * as actions from '../../actions';
import { Helmet } from 'react-helmet';
import { Matches } from '../../utils/matches';
import { GroupContainer, ImageRenderer, MatchResult, TitleContainer } from './MatchGallery';
import { CompetitionEvolution } from './CompetitionEvolution';
import { useAppDispatch } from '../../utils/modules';

interface IGalleryContainer {
	imgSrc: string;
}
const GalleryContainer = styled.div<IGalleryContainer>`
	font-family: Champions;
	flex: 1;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background: ${props => `url('../img/background/${props.imgSrc}.jpg') no-repeat center center fixed`}; 
	-webkit-background-size: cover;
	-moz-background-size: cover;
	-o-background-size: cover;
	background-size: cover;
`;

const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	overflow-x: hidden;
`;

const ScarfContainer = styled.div`
	display: flex; 
	flex-direction: column;
	margin-top: 6px;
	max-height: auto;
	overflow: hidden;
`;

const TitleSectionContainer = styled.div`
	margin: auto;
	text-shadow: 2px 2px 2px black;
`;

const MatchResultContainer = styled.div`
	display: flex;
	flex-direction: row;
	max-width: 700px;
	margin: auto;
	flex-flow: row wrap;
`;

export const ScarfCommentContainer = styled.div`
	font-size: 12px;
	margin: auto;
	background-color: rgba(255, 255, 255, 0.4);
	color: black;
	padding: 5px 3px;
	border-radius: 4px;
	max-width: 80%;
`;

export default function RMMatchGallery() {

	const dispatch = useAppDispatch();
	
	const [imgNumber, setImgNumber] = useState(0);

	const [currentMatchScarfGroups, setCurrentMatchScarfGroups] = useState<IMatchScarvesGroup[]>([]);
	const [currentStartYear, setCurrentStartYear] = useState(0);
	const [currentEndYear, setCurrentEndYear] = useState(0);

	useEffect(() => {
		setImgNumber(Math.floor(Math.random() * 8) + 1);
		dispatch(actions.setSectionName(EnumSection.matches))
	}, [dispatch]);

	const useMobileViewActive = useSelector(
		(completeState: IStore): boolean => {
			return completeState ? completeState.ui.isMobileViewActive : false
		});

	const viewPort = useSelector(
		(completeState: IStore): IViewPort => {
			return completeState ? completeState.ui.viewPort: { width: 0, height: 0 }
		});

	const matchScarfGroups = useSelector(
		(completeState: IStore): IMatchScarvesGroup[] => {
			return completeState ? completeState.matches.currentRMMatchScarfGroups : []
		});


	const { startYear, endYear } = useMemo(
		(): {
			startYear: number;
			endYear: number;
		} => {
			let startYear = 2000;
			let endYear = 2010;
			matchScarfGroups.forEach(match => {
				if (match.yearEnd > endYear) {
					endYear = match.yearEnd;
				}
				if (match.yearEnd < startYear) {
					startYear = match.yearEnd;
				}
			});
			setCurrentEndYear(endYear);
			setCurrentStartYear(startYear);
			return {
				startYear,
				endYear
			};
		},
		[matchScarfGroups]
	);

	useEffect(() => {
		setCurrentMatchScarfGroups(matchScarfGroups);
	}, [matchScarfGroups]);	

	const renderMatchScarves = useCallback((matchScarfGroup: IMatchScarvesGroup, index: number) => {
		let title = `${matchScarfGroup.yearEnd}`;

		if (matchScarfGroup.competition) {
			title += ` ${Matches.parseCompetitionTitleByYear(matchScarfGroup.competition, matchScarfGroup.yearEnd)}`;
			if (matchScarfGroup.matches.length > 0 && matchScarfGroup.matches[0].round) {
				title += ` ${Matches.getNameOfPhase(matchScarfGroup.matches[0].round)}`;
			}
		}

		if (!matchScarfGroup.competition) {
			return null;
		}
		let code = `${matchScarfGroup.competition.region_code}_${matchScarfGroup.competition.competition_code}`;
		if (matchScarfGroup.competition.isInternational) {
			code += `_${matchScarfGroup.yearEnd}`;
		}
		return (
			<GroupContainer key={index} $useMobileViewActive={useMobileViewActive}>
				<TitleContainer $useMobileViewActive={useMobileViewActive}>
					<TitleSectionContainer>
						<img
							title={title}
							src={`../../../img/shield/${code}.png`}
							alt={title}
						/>
					</TitleSectionContainer>
					<TitleSectionContainer>{title}</TitleSectionContainer>
				</TitleContainer>

				
				<MatchResultContainer>
					{matchScarfGroup.matches.map((x, i) => {
						return (
							<MatchResult
								yearEnd={matchScarfGroup.yearEnd}
								match={x}
								index={i}
								firstTeam={matchScarfGroup.firstTeam}
								secondTeam={matchScarfGroup.secondTeam}
								thirdTeam={matchScarfGroup.thirdTeam}
								fourthTeam={matchScarfGroup.fourthTeam}
								otherTeams={matchScarfGroup.otherTeams}
							/>
						);})
					}
				</MatchResultContainer>
				<ScarfContainer>
					{
						matchScarfGroup.scarfCodes.map((x, i) => {
							return (
								<ImageRenderer
									key={i}
									src={`../../../img/scarf/${x}.jpg`}
									title={`${matchScarfGroup.firstTeam?.name} vs ${matchScarfGroup.secondTeam?.name}`}
									alt={`${matchScarfGroup.firstTeam?.name} vs ${matchScarfGroup.secondTeam?.name}`}
									isMobileViewActive={useMobileViewActive}
								/>
							);
						})
					}
					{matchScarfGroup.comment && <ScarfCommentContainer>{matchScarfGroup.comment}</ScarfCommentContainer>}
				</ScarfContainer>
			</GroupContainer>
		);
	}, [useMobileViewActive]);


	const onChangeRange = useCallback((startIndex: number, endIndex: number) => {
		const changedStartYear = startYear + startIndex;
		const changedEndYear = startYear + endIndex;
		setCurrentStartYear(changedStartYear);
		setCurrentEndYear(changedEndYear);

		const filteredCurrentMatchScarfGroups = matchScarfGroups.filter(scarfGroup => scarfGroup.yearEnd <= changedEndYear && scarfGroup.yearEnd >= changedStartYear);
		setCurrentMatchScarfGroups(filteredCurrentMatchScarfGroups);
	}, [startYear, matchScarfGroups]);

	return (
		<>
			<Helmet>
				<title>{'Scarves from football games - FootballScarfMuseumCollection'}</title>
			</Helmet>
			<GalleryContainer imgSrc={`RM_${imgNumber}`}>
				<CompetitionEvolution
					viewPortX={viewPort.width}
					viewPortY={viewPort.height}
					startYear={startYear}
					endYear={endYear}
					currentStartYear={currentStartYear}
					currentStartYearText={currentStartYear.toString()}
					currentEndYearText={currentEndYear.toString()}
					currentEndYear={currentEndYear}
					initEndIndex={endYear - startYear}
					initStartIndex={0}
					totalGaps={endYear - startYear + 1}
					onChangeRange={onChangeRange}
				/>
				<ContentContainer>
					{currentMatchScarfGroups.map((x, i) => renderMatchScarves(x, i))}
				</ContentContainer>
			</GalleryContainer>
		</>
	);
}
